export type Activity = {
    taskName?: string;
    activityName: string;
    pillar: string;
    totalFTEs?: number;
    automationPercent?: number;
    benchmarkAutomation?: number;
    benchmark_percent?: number;
    benchmarkPercent?: number;
};

type PillarAlloc = {
    ftesByRegion?: Record<string, number>;
    automationPercentage?: number;
};

type BenchRow = {
    pillar?: string;
    name?: string;
    benchmark_percent?: number;
    benchmarkAutomation?: number;
    percent?: number;
    task_label: string;
};

// Heuristic normalizer:
// - Accepts either 0–100 or 0–1 ratios and returns 0–100.
// - Treat NaN/Infinity as 0.
function normalizePercent(x: unknown): number {
  const v = Number(x);
  if (!Number.isFinite(v) || v < 0) return 0;
  if (v > 1) return v;          // assume already 0–100
  if (v === 0) return 0;
  return v * 100;               // treat 0–1 as ratio → %
}

function normKey(s: string): string {
  return (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '');
}

export function getBenchmarkForActivity(activity: Activity, rows: BenchRow[] = []): number {
    if (!activity || !rows?.length) return 0;

    const activityKey = normKey(activity.activityName || '');
    const pillarKey = normKey(activity.pillar || '');

    if (['otherprocesses', 'other'].includes(pillarKey)) {
        return 0;
    }

    // 1️⃣ Try to match by exact task label first
    const directMatch = rows.find(b => normKey(b.task_label || b.name || '') === activityKey);
    if (directMatch) {
        return normalizePercent(
            directMatch.benchmark_percent ?? directMatch.benchmarkAutomation ?? directMatch.percent ?? 0
        );
    }

    // 2️⃣ Fallback: match by pillar if activity not found
    const pillarMatches = rows.filter(b => normKey(b.pillar || b.name || '') === pillarKey);
    if (pillarMatches.length) {
        const sum = pillarMatches.reduce((s, b) => {
            const raw = b.benchmark_percent ?? b.benchmarkAutomation ?? b.percent ?? 0;
            return s + normalizePercent(raw);
        }, 0);
        return sum / pillarMatches.length;
    }

    // 3️⃣ Nothing found
    return 0;
}


export function computeMixedWeightedAutomation(
    activitiesForCalculation: Activity[],
    pillarFTEAllocation: Record<string, PillarAlloc> | undefined,
    benchmarkRows: BenchRow[]
): {
    clientWeighted: number;
    benchmarkWeighted: number;
    totalFTEsMixed: number;
} {
    // 1) Activity-level accumulation
    let clientWeightedSum = 0;
    let benchWeightedSum = 0;
    let totalFTEs = 0;
    const pillarsCovered = new Set<string>();

    // 2) Pillar-level fallback for uncovered pillars
    let pillarClientWeightedSum = 0;
    let pillarBenchWeightedSum = 0;
    let pillarTotalFTEs = 0;

    console.log(`\n🔍 [BENCHMARK CALC] computeMixedWeightedAutomation called`)
    console.log(`   Input activities count: ${(activitiesForCalculation || []).length}`)
    console.log(`   pillarFTEAllocation keys:`, Object.keys(pillarFTEAllocation || {}))

    const filteredActivities = (activitiesForCalculation || []).filter((a: Activity) => {
        const name = (a.taskName || a.activityName || '').toLowerCase();
        const isPillarLevel =
            name.includes('(pillar-level)') ||
            (!!pillarFTEAllocation && Object.keys(pillarFTEAllocation).some(p =>
                p.toLowerCase() === (a.pillar || '').toLowerCase()
            ));

        if (isPillarLevel) {
            console.log(`   ❌ FILTERED OUT: "${a.activityName || a.taskName}" (pillar: "${a.pillar}", FTEs: ${a.totalFTEs})`)
        } else {
            console.log(`   ✅ KEPT: "${a.activityName || a.taskName}" (pillar: "${a.pillar}", FTEs: ${a.totalFTEs})`)
        }

        return !isPillarLevel; // only keep task-level items
    });

    console.log(`   Filtered activities count: ${filteredActivities.length}`)

    for (const a of filteredActivities || []) {
        const ftes = Number(a.totalFTEs) || 0;
        if (ftes <= 0) continue;

        const clientPct = normalizePercent(
            Number.isFinite(a.automationPercent) ? Number(a.automationPercent) : 0
        );
        const benchPct = normalizePercent(
            Number.isFinite(a.benchmarkAutomation) ? Number(a.benchmarkAutomation)
                : Number.isFinite((a as any).benchmark_percent) ? Number((a as any).benchmark_percent)
                    : Number.isFinite((a as any).benchmarkPercent) ? Number((a as any).benchmarkPercent)
                        : getBenchmarkForActivity(a, benchmarkRows)
        );

        clientWeightedSum += ftes * clientPct;
        benchWeightedSum  += ftes * benchPct;
        totalFTEs += ftes;
        if (a.pillar) pillarsCovered.add(a.pillar);
    }


    if (pillarFTEAllocation) {
        for (const [pillarName, pillar] of Object.entries(pillarFTEAllocation)) {
            if (pillarsCovered.has(pillarName)) continue;
            const byRegion = pillar.ftesByRegion || {};
            const ftes = Object.values(byRegion).reduce((s, v) => s + (Number(v) || 0), 0);
            if (ftes <= 0) continue;

            const clientPct = Number(pillar.automationPercentage) || 0;
            const benchPct = 70 //avgBenchmarkForPillar(pillarName, benchmarkRows);

            pillarClientWeightedSum += ftes * clientPct;
            pillarBenchWeightedSum  += ftes * benchPct;
            pillarTotalFTEs += ftes;
        }
    }



    // 3) Combine
    const totalFTEsMixed = totalFTEs + pillarTotalFTEs;
    if (totalFTEsMixed <= 0) {
        return { clientWeighted: 0, benchmarkWeighted: 0, totalFTEsMixed: 0 };
    }

    const clientWeighted = Math.round((clientWeightedSum + pillarClientWeightedSum) / totalFTEsMixed);
    const benchmarkWeighted = Math.round((benchWeightedSum + pillarBenchWeightedSum) / totalFTEsMixed);

    console.log(`\n📊 [BENCHMARK CALC] Final Results:`)
    console.log(`   Activity-level FTEs: ${totalFTEs}`)
    console.log(`   Pillar-level FTEs: ${pillarTotalFTEs}`)
    console.log(`   Total FTEs Mixed: ${totalFTEsMixed}`)
    console.log(`   Client Weighted: ${clientWeighted}%`)
    console.log(`   Benchmark Weighted: ${benchmarkWeighted}%`)

    return { clientWeighted, benchmarkWeighted, totalFTEsMixed };
}


